"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { usePathname, useRouter } from "next/navigation";
import ImageWrapper from "./ui/ImageWrapper";
import { fetchAllNews, fetchNewsAutocomplete, NewsAutocompleteItem } from "@/app/utilities/apis";

export default function NewsSearchBox({
  initialSearchQuery,
  basePath,
  tagIds,
  isBrandOrModelNews,
}: {
  initialSearchQuery?: string;
  basePath?: string;
  tagIds?: string; // Brand/model ids of the current page.
  isBrandOrModelNews?: boolean;
}) {
  const router = useRouter();
  const pathname = usePathname();

  // Resolve base path from prop or current pathname (stripping /page-\d+ pagination segment)
  const resolvedBasePath = basePath || pathname?.replace(/\/page-\d+.*$/, "") || "/news";

  const [query, setQuery] = useState(initialSearchQuery ?? "");
  const [suggestions, setSuggestions] = useState<NewsAutocompleteItem[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync state if initialSearchQuery prop changes
  useEffect(() => {
    if (initialSearchQuery !== undefined) {
      setQuery(initialSearchQuery);
    }
  }, [initialSearchQuery]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced API call for autocomplete when query length > 2
  useEffect(() => {
    const trimmed = query.trim();

    if (trimmed.length <= 2) {
      setSuggestions([]);
      setShowDropdown(false);
      setIsLoading(false);
      setSelectedIndex(-1);
      return;
    }

    setIsLoading(true);
    let isCurrent = true;

    const delayDebounce = setTimeout(async () => {
      try {
        const res = await fetchNewsAutocomplete(trimmed);
        if (isCurrent) {
          if (res?.success && Array.isArray(res.data) && res.data.length > 0) {
            setSuggestions(res.data);
            setShowDropdown(true);
          } else {
            setSuggestions([]);
            setShowDropdown(true);
          }
          setSelectedIndex(-1);
        }
      } catch {
        if (isCurrent) {
          setSuggestions([]);
        }
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => {
      isCurrent = false;
      clearTimeout(delayDebounce);
    };
  }, [query]);

  // Navigate on suggestion select
  const handleSelectSuggestion = useCallback(
    (item: NewsAutocompleteItem) => {
      setShowDropdown(false);
      setQuery(item.name);

      let targetUrl = resolvedBasePath;
      if (item.type === "model" && item.brandSlug && item.slug) {
        targetUrl = `/${item.brandSlug}/${item.slug}/news`;
      } else if (item.type === "brand" && item.slug) {
        targetUrl = `/${item.slug}/news`;
      } else if (item.brandSlug && item.slug) {
        targetUrl = `/${item.brandSlug}/${item.slug}/news`;
      } else if (item.slug) {
        targetUrl = `/${item.slug}/news`;
      } else {
        targetUrl = `${resolvedBasePath}?q=${encodeURIComponent(item.name || query.trim())}`;
      }

      router.push(targetUrl);
    },
    [query, resolvedBasePath, router]
  );

  // Form submit (standard search behavior)
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowDropdown(false);

    // If an item is active via keyboard arrow navigation, select that
    if (selectedIndex >= 0 && suggestions[selectedIndex]) {
      handleSelectSuggestion(suggestions[selectedIndex]);
      return;
    }

    const trimmed = query.trim();
    if (!trimmed) {
      router.push(resolvedBasePath);
      return;
    }

    // When searching on brand or model news page, check if any article matches for this brand/model
    if (isBrandOrModelNews && tagIds) {
      setIsLoading(true);
      try {
        const checkRes = await fetchAllNews(1, 1, '', trimmed, '', tagIds);
        const hasMatch = checkRes?.news && checkRes.news.length > 0;
        if (!hasMatch) {
          // No match found for this brand/model -> route directly to base news page with that q parameter
          router.push(`/news?q=${encodeURIComponent(trimmed)}`);
          return;
        }
      } catch {
        // Fallback to normal route
      } finally {
        setIsLoading(false);
      }
    }

    router.push(`${resolvedBasePath}?q=${encodeURIComponent(trimmed)}`);
  };

  // Keyboard navigation (ArrowDown, ArrowUp, Enter, Escape)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1
      );
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const handleClearSearch = () => {
    setQuery("");
    setSuggestions([]);
    setShowDropdown(false);
    setSelectedIndex(-1);
    router.push(resolvedBasePath);
  };

  const inputPlaceholder = resolvedBasePath.includes("car-expert-reviews")
    ? "Search expert reviews by title, brand, or model..."
    : "Search news by title, brand, or model...";

  return (
    <div
      ref={containerRef}
      className="relative w-full md:max-w-md lg:max-w-lg xl:max-w-[542px] mt-0 md:mt-3 z-30"
    >
      <form onSubmit={handleSearch} className="relative">
        <span className="absolute left-5 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <ImageWrapper
            src={"/search.svg"}
            alt="Search"
            title="Search"
            width={18}
            height={18}
            cdnBaseUrl={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}`}
          />
        </span>

        <input
          ref={inputRef}
          type="text"
          placeholder={inputPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (query.trim().length > 2 && suggestions.length > 0) {
              setShowDropdown(true);
            }
          }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          className="w-full h-[40px] md:h-[48px] pl-[44px] pr-12 rounded-[12px] md:rounded-lg bg-[#fff] border border-[#fff] outline-none text-[#000] placeholder-[#71717A] text-[14px] font-[500] leading-[16px] transition-colors shadow-sm focus:ring-2 focus:ring-white/40"
        />

        {isLoading ? (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : query ? (
          <button
            type="button"
            onClick={handleClearSearch}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#8A94A6] hover:text-[#000] transition-colors p-1"
            aria-label="Clear search"
          >
            ✕
          </button>
        ) : null}
      </form>

      {/* Autocomplete Dropdown */}
      {showDropdown && query.trim().length > 2 && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1.5 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden text-left max-h-[280px] overflow-y-auto">
          <ul className="py-1">
            {suggestions.map((item, index) => {
              const isSelected = selectedIndex === index;

              return (
                <li key={`${item.type}-${item.slug}-${index}`}>
                  <button
                    type="button"
                    onClick={() => handleSelectSuggestion(item)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`w-full px-4 py-2.5 text-left text-[14px] text-gray-800 transition-colors cursor-pointer ${
                      isSelected ? "bg-gray-100 font-medium" : "hover:bg-gray-50 font-normal"
                    }`}
                  >
                    {item.name}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}